import { parseIDL } from "@foxglove/omgidl-parser";
import { MessageReader as OmgidlMessageReader } from "@foxglove/omgidl-serialization";
import { parseRos2idl } from "@foxglove/ros2idl-parser";
import { parse as parseMessageDefinition } from "@foxglove/rosmsg";
import { MessageReader as ROS1MessageReader } from "@foxglove/rosmsg-serialization";
import { MessageReader as ROS2MessageReader } from "@foxglove/rosmsg2-serialization";
import { McapTypes } from "@mcap/core";

import {
  decodeCompressedVideoMessage,
  FOXGLOVE_COMPRESSED_VIDEO_SCHEMA_NAME,
} from "./foxgloveCompressedVideo";
import { decodeMsgpackMessage } from "./msgpack";

export type Deserializer = (data: ArrayBufferView) => unknown;

const textDecoder = new TextDecoder();

/**
 * https://mcap.dev/spec/registry#schema-encodings
 */
enum SchemaEncoding {
  Empty = "",
  Flatbuffer = "flatbuffer",
  Json = "jsonschema",
  Omgidl = "omgidl",
  Protobuf = "protobuf",
  Ros1msg = "ros1msg",
  Ros2idl = "ros2idl",
  Ros2msg = "ros2msg",
}

/**
 * https://mcap.dev/spec/registry#message-encodings
 */
enum MessageEncoding {
  Cdr = "cdr",
  Flatbuffer = "flatbuffer",
  Json = "json",
  Msgpack = "msgpack",
  Protobuf = "protobuf",
  Ros1 = "ros1",
}

function deserializeJsonMsgType(): Deserializer {
  return (data: ArrayBufferView) =>
    JSON.parse(textDecoder.decode(data)) as JsonValue;
}

function deserializeMsgpackMsgType(): Deserializer {
  // Msgpack is self-describing, like JSON: its (jsonschema or empty) schema
  // is descriptive metadata only, so decoding needs no schema compilation.
  return decodeMsgpackMessage;
}

function deserializeFlatbufferMsgType(): Deserializer {
  throw new Error("Flatbuffer deserialization not yet implemented");
}

function deserializeOmgIdlMsgType(schema: McapTypes.Schema): Deserializer {
  const reader = new OmgidlMessageReader(
    schema.name,
    parseIDL(textDecoder.decode(schema.data)),
  );
  return (data: ArrayBufferView) => reader.readMessage(data);
}

function deserializeProtobufMsgType(schema: McapTypes.Schema): Deserializer {
  // General protobuf deserialization is not implemented, but
  // foxglove.CompressedVideo (how the Foxglove SDK records video) is a
  // fixed four-field schema that is hand-decoded without a protobuf runtime.
  if (schema.name === FOXGLOVE_COMPRESSED_VIDEO_SCHEMA_NAME) {
    return (data: ArrayBufferView) => decodeCompressedVideoMessage(data);
  }
  throw new Error(
    `Protobuf deserialization not implemented (schema: ${schema.name})`,
  );
}

function deserializeRos1MsgType(schema: McapTypes.Schema): Deserializer {
  const reader = new ROS1MessageReader(
    parseMessageDefinition(textDecoder.decode(schema.data)),
  );
  return (data: ArrayBufferView) => reader.readMessage(data);
}

function deserializeRos2IdlMsgType(schema: McapTypes.Schema): Deserializer {
  const reader = new ROS2MessageReader(
    parseRos2idl(textDecoder.decode(schema.data)),
  );
  return (data: ArrayBufferView) => reader.readMessage(data);
}

function deserializeRos2MsgType(schema: McapTypes.Schema): Deserializer {
  const reader = new ROS2MessageReader(
    parseMessageDefinition(textDecoder.decode(schema.data), { ros2: true }),
  );
  return (data: ArrayBufferView) => reader.readMessage(data);
}

/**
 * Deserializer factory per legal (message encoding, schema encoding) pair.
 *
 * Neither encoding alone identifies a decoder: msgpack and JSON channels both
 * carry `jsonschema` (or empty) schemas, and `cdr` payloads are described by
 * three different schema languages. Keying on the pair makes the table both
 * the validator and the dispatcher — an absent entry IS the illegal pairing,
 * with no separate allowlist or precedence order to keep in sync.
 */
const PAIR_TO_DESERIALIZER: {
  [M in MessageEncoding]: Partial<{
    [S in SchemaEncoding]: (schema: McapTypes.Schema) => Deserializer;
  }>;
} = {
  [MessageEncoding.Cdr]: {
    [SchemaEncoding.Omgidl]: deserializeOmgIdlMsgType,
    [SchemaEncoding.Ros2idl]: deserializeRos2IdlMsgType,
    [SchemaEncoding.Ros2msg]: deserializeRos2MsgType,
  },
  [MessageEncoding.Flatbuffer]: {
    [SchemaEncoding.Flatbuffer]: deserializeFlatbufferMsgType,
  },
  [MessageEncoding.Json]: {
    [SchemaEncoding.Empty]: deserializeJsonMsgType,
    [SchemaEncoding.Json]: deserializeJsonMsgType,
  },
  [MessageEncoding.Msgpack]: {
    [SchemaEncoding.Empty]: deserializeMsgpackMsgType,
    [SchemaEncoding.Json]: deserializeMsgpackMsgType,
  },
  [MessageEncoding.Protobuf]: {
    [SchemaEncoding.Protobuf]: deserializeProtobufMsgType,
  },
  [MessageEncoding.Ros1]: {
    [SchemaEncoding.Ros1msg]: deserializeRos1MsgType,
  },
};

function messageEncodingIsKnown(
  messageEncoding: string,
): messageEncoding is MessageEncoding {
  const known: string[] = Object.values(MessageEncoding);
  return known.includes(messageEncoding);
}

function schemaEncodingIsKnown(
  schemaEncoding: string,
): schemaEncoding is SchemaEncoding {
  const known: string[] = Object.values(SchemaEncoding);
  return known.includes(schemaEncoding);
}

export function makeDeserializer(
  channel: McapTypes.Channel,
  schema: McapTypes.Schema,
): Deserializer {
  const messageEncoding = channel.messageEncoding;
  if (!messageEncodingIsKnown(messageEncoding)) {
    throw new Error(`Unknown message encoding ${channel.messageEncoding}`);
  }

  const schemaEncoding = schema.encoding;
  if (!schemaEncodingIsKnown(schemaEncoding)) {
    throw new Error(`Unknown schema encoding ${schema.encoding}`);
  }

  const deserializerFactory =
    PAIR_TO_DESERIALIZER[messageEncoding][schemaEncoding];
  if (deserializerFactory === undefined) {
    throw new Error(
      `Schema encoding ${schemaEncoding} not allowed for message encoding ${channel.messageEncoding}`,
    );
  }

  return deserializerFactory(schema);
}
